/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect, useMemo } from "react";
import useDataState from "../../../hooks/useDataState";
import { createSupabaseClient } from "../../../hooks/useSupabase";
import {
  buildCutoffMap,
  buildSpecialtyToUsersMap,
  isAllSpecialtiesCovered,
  CutoffRow,
  CutoffState,
  SpecialtyRow,
} from "../../../helpers/radiologistAvailability";

type StatAvailabilityData = {
  specialtyToUsers: Record<string, string[]>;
  cutoffMap: Record<string, Record<string, CutoffState>>;
};

const useStatAvailability = (params: {
  centerId: string | null;
  requiredSpecialties: string[];
  dateRange: { start: string; end: string } | null;
  skip: boolean;
}) => {
  const { centerId, requiredSpecialties, dateRange, skip } = params;

  const supabase = useMemo(() => createSupabaseClient(), []);

  const [dataState, updateDataState] = useDataState<StatAvailabilityData>();

  const fetchAvailability = useCallback(async () => {
    if (
      skip ||
      !centerId ||
      !dateRange ||
      requiredSpecialties.length === 0 ||
      !supabase
    ) {
      return;
    }

    try {
      updateDataState({ status: "loading" });

      const specialtiesResponse = await supabase
        .from("radiologist_specialties")
        .select("center_user_id,specialty")
        .eq("center_id", centerId)
        .in("specialty", requiredSpecialties)
        .returns<SpecialtyRow[]>();

      if (specialtiesResponse.error) {
        throw new Error(specialtiesResponse.error.message);
      }

      const specialtyRows = specialtiesResponse.data ?? [];
      const radiologistIds = Array.from(
        new Set(specialtyRows.map((r) => r.center_user_id)),
      );

      let cutoffRows: CutoffRow[] = [];

      if (radiologistIds.length > 0) {
        const cutoffsResponse = await supabase
          .from("radiologist_read_cutoffs")
          .select("center_user_id,date,type,cutoff_minutes_from_midnight")
          .eq("center_id", centerId)
          .in("center_user_id", radiologistIds)
          .gte("date", dateRange.start)
          .lte("date", dateRange.end)
          .returns<CutoffRow[]>();

        if (cutoffsResponse.error) {
          throw new Error(cutoffsResponse.error.message);
        }

        cutoffRows = cutoffsResponse.data ?? [];
      }

      const specialtyToUsers = buildSpecialtyToUsersMap(specialtyRows);
      const cutoffMap = buildCutoffMap(cutoffRows);

      updateDataState({
        status: "success",
        data: { specialtyToUsers, cutoffMap },
      });
    } catch (err) {
      updateDataState({ status: "error", error: (err as Error).message });
    }
  }, [
    skip,
    centerId,
    dateRange,
    requiredSpecialties,
    supabase,
    updateDataState,
  ]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const isAvailable = useCallback(
    (dateStr: string, minutesLocal: number): boolean => {
      if (skip) return true;
      if (dataState.status !== "success") return true;

      const { specialtyToUsers, cutoffMap } = dataState.data;

      return isAllSpecialtiesCovered(
        requiredSpecialties,
        specialtyToUsers,
        cutoffMap,
        dateStr,
        minutesLocal,
      );
    },
    [skip, dataState, requiredSpecialties],
  );

  return useMemo(() => ({ isAvailable }), [isAvailable]);
};

export default useStatAvailability;
